import React, { type ReactElement, type RefObject } from 'react';

import { styled } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import { Label } from '@atlaskit/form/label/default';
import Heading from '@atlaskit/heading/heading';
import UploadIcon from '@atlaskit/icon/core/upload';
import type { Placement } from '@atlaskit/popper/main';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { useIsDisabledContext } from '../../../../controllers/disabled-context';
import { FormFieldErrorMessage } from '../../../config-panel/form-field-error-messages';
import { PanelSubheader } from '../../../config-panel/panel-subheader';
import { MediaContentHelperMessage } from '../../media-picker/ui/helper-message';
import { DefaultImagePicker } from '../default-image-picker';
import { UnsplashSearchBar } from '../search-bar';
import type { UnsplashResult } from '../types';
import { useUnsplash } from '../utils/use-unsplash';

import { messages } from './messages';

export type SelectedImage = {
	altDescription: string;
	downloadLink?: string;
	fullSizeImageUrl: string;
};

export type UnsplashSearchProps = {
	analyticsActionSubject: string;
	analyticsActionSubjectId: string;
	analyticsSource: string;
	confluencePageId?: string;
	contentId: string;
	dropdownPopupComponentRef?: RefObject<HTMLDivElement | null>;
	enableBottomSpacing?: boolean;
	handleImageSelection: (props: SelectedImage) => void;
	helperMessage?: string | ReactElement;
	id?: string;
	idPrefix: string;
	imageAltText?: string;
	initialUnsplashResults?: UnsplashResult[] | undefined;
	isDisabled?: boolean;
	isNoImageAllowed: boolean;
	isPageTitleComponent?: boolean;
	isUploadingImage?: boolean;
	openMediaBrowser?: () => void;
	placement?: Placement;
	sectionLabel: string | ReactElement;
	selectedImageUrl?: string;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashSearchFlexContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UploadButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		height: '40px',
		width: '40px',
	},
});

const bottomSpacing = xcss({ marginBottom: 'space.100' });
const removeBottomSpacing = xcss({ marginBottom: 'space.0' });

export const UnsplashSearch = ({
	id,
	idPrefix,
	imageAltText,
	sectionLabel,
	helperMessage,
	selectedImageUrl,
	initialUnsplashResults,
	handleImageSelection,
	openMediaBrowser,
	dropdownPopupComponentRef,
	placement = 'bottom',
	contentId,
	confluencePageId,
	analyticsSource,
	analyticsActionSubject,
	analyticsActionSubjectId,
	isPageTitleComponent = false,
	isUploadingImage = false,
	isNoImageAllowed,
	enableBottomSpacing = true,
}: UnsplashSearchProps): React.JSX.Element => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isDisabled = useIsDisabledContext();
	const {
		selectedDefaultImageUrl,
		setDefaultUnsplashResults,
		defaultUnsplashResults,
		unSplashSearchResults,
		setUnsplashSearchResults,
		setError,
		handleError,
		errorMessage,
	} = useUnsplash(isPageTitleComponent, selectedImageUrl, initialUnsplashResults);

	const unsplashSearchFieldId = id
		? `${idPrefix}-search-unsplash-${id}`
		: `${idPrefix}-search-unsplash`;

	const handleUploadButtonClick = () => {
		openMediaBrowser && openMediaBrowser();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: analyticsActionSubject,
				actionSubjectId: analyticsActionSubjectId,
				source: analyticsSource,
			},
		}).fire();
	};

	return (
		<UnsplashSearchFlexContainer>
			{expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true) ? (
				<Heading size="xsmall" as="h2">
					{sectionLabel}
				</Heading>
			) : (
				<Label htmlFor={unsplashSearchFieldId}>
					<PanelSubheader showUpdatedStyles>{sectionLabel}</PanelSubheader>
				</Label>
			)}
			<MediaContentHelperMessage helperMessage={helperMessage} isDisabled={isDisabled} />
			<Inline space="space.050" alignBlock="center">
				<UnsplashSearchBar
					unsplashSearchFieldId={unsplashSearchFieldId}
					elementIdPrefix={idPrefix}
					handleImageSelection={handleImageSelection}
					analyticsSource={analyticsSource}
					setDefaultUnsplashResults={setDefaultUnsplashResults}
					defaultUnsplashResults={defaultUnsplashResults}
					unSplashSearchResults={unSplashSearchResults}
					setUnsplashSearchResults={setUnsplashSearchResults}
					setError={setError}
					handleError={handleError}
					placement={placement}
					dropdownPopupComponentRef={dropdownPopupComponentRef}
					isUploadingImage={isUploadingImage}
				/>
				{!!openMediaBrowser && (
					<UploadButtonWrapper>
						<IconButton
							onClick={handleUploadButtonClick}
							testId="open-media-browser"
							isDisabled={isUploadingImage || isDisabled}
							icon={() => <UploadIcon color="currentColor" spacing="spacious" label="" />}
							label={intl.formatMessage(messages.uploadMediaLabel)}
						/>
					</UploadButtonWrapper>
				)}
			</Inline>
			<Stack>
				<Box xcss={enableBottomSpacing ? bottomSpacing : removeBottomSpacing}>
					<DefaultImagePicker
						contentId={contentId}
						confluencePageId={confluencePageId}
						initialUnsplashResults={defaultUnsplashResults?.slice(0, 5)}
						handleImageSelection={handleImageSelection}
						imageAltText={imageAltText}
						selectedImageUrl={selectedDefaultImageUrl}
						analyticsSource={analyticsSource}
						isUploadingImage={isUploadingImage}
						isNoImageAllowed={isNoImageAllowed && !isPageTitleComponent}
					/>
				</Box>
				{errorMessage && <FormFieldErrorMessage>{errorMessage}</FormFieldErrorMessage>}
			</Stack>
		</UnsplashSearchFlexContainer>
	);
};
